import React, { useEffect, useState } from 'react';

import BulletList from '@tiptap/extension-bullet-list';
import CharacterCount from '@tiptap/extension-character-count';
import HardBreak from '@tiptap/extension-hard-break';
import Highlight from '@tiptap/extension-highlight';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Link from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Strike from '@tiptap/extension-strike';

import 'prosemirror-view/style/prosemirror.css';
import '../utils/texteditor.style.css';
import { TableKit } from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyleKit } from '@tiptap/extension-text-style';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  Code2,
  ChevronDown,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Highlighter,
  Minus,
  CornerDownLeft,
  Link2,
  Baseline,
  TableIcon,
} from 'lucide-react';

interface Props {
  value: string;
  onChange: (value: string) => void;
}

interface HeadingLevel {
  value: 'paragraph' | 1 | 2 | 3 | 4 | 5 | 6;
}

const Button = ({
  icon,
  isActive,
  onClick,
  label,
}: {
  icon: React.ReactNode;
  isActive?: boolean;
  onClick: () => void;
  label: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`p-2 rounded cursor-pointer hover:bg-gray-100 transition ${
      isActive ? 'bg-gray-200 text-blue-600' : 'text-gray-600'
    }`}
    title={label}
  >
    {icon}
  </button>
);

const TextEditor: React.FC<Props> = ({ value, onChange }) => {
  const [showHeadingDropdown, setShowHeadingDropdown] = useState(false);
  const [showAlignDropdown, setShowAlignDropdown] = useState(false);
  const [showTextColorPalette, setShowTextColorPalette] = useState(false);
  const [showHighlightColorPalette, setShowHighlightColorPalette] =
    useState(false);
  const [showFontDropdown, setShowFontDropdown] = useState(false);
  const [showFontSizeDropdown, setShowFontSizeDropdown] = useState(false);

  const textColors = [
    '#000000',
    '#FF0000',
    '#00FF00',
    '#0000FF',
    '#FFFF00',
    '#FF00FF',
    '#00FFFF',
    '#C0C0C0',
    '#808080',
    '#800000',
    '#808000',
    '#008000',
    '#000080',
    '#800080',
    '#008080',
    '#FF1493',
    '#FFD700',
    '#32CD32',
    '#8A2BE2',
    '#FF4500',
    '#2F4F4F',
    '#B22222',
    '#9932CC',
    '#8B0000',
  ];

  const [charCount, setCharCount] = useState(0);
  const [wordCount, setWordCount] = useState(0);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: false,
        orderedList: false,
        strike: false,
        listItem: false,
        hardBreak: false,
        horizontalRule: false,
        link: false,
      }),
      BulletList.configure({
        HTMLAttributes: {
          class: 'list-disc',
        },
        itemTypeName: 'listItem',
      }),
      OrderedList,
      ListItem,
      CharacterCount,
      Strike,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight.configure({
        multicolor: true,
      }),
      HardBreak.configure({ keepMarks: true }),
      HorizontalRule,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
      }),
      TextStyleKit,
      TableKit.configure({
        table: {
          resizable: true,
        },
      }),
    ],
    content: value,
    onCreate: ({ editor }) => {
      setCharCount(editor.storage.characterCount.characters());
      setWordCount(editor.storage.characterCount.words());
    },
    onUpdate: ({ editor }) => {
      const htmlContent = editor.getHTML();
      onChange(htmlContent);
      setCharCount(editor.storage.characterCount.characters());
      setWordCount(editor.storage.characterCount.words());
    },
    editorProps: {
      attributes: {
        class: 'prose focus:outline-none',
      },
      handleDOMEvents: {
        mousedown: (view, event) => {
          if (event.button !== 0) {
            return false;
          }
          view.focus();
          return true;
        },
      },
    },
  });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (!target.closest('[data-dropdown]')) {
        setShowHeadingDropdown(false);
        setShowFontDropdown(false);
        setShowFontSizeDropdown(false);
        setShowAlignDropdown(false);
        setShowTextColorPalette(false);
        setShowHighlightColorPalette(false);
      }
    };

    window.addEventListener('click', handleClickOutside);
    return () => {
      window.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (editor && editor.getHTML() !== value) {
      editor.commands.setContent(value);
      setCharCount(editor.storage.characterCount.characters());
      setWordCount(editor.storage.characterCount.words());
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  const applyHeading = (level: HeadingLevel['value']) => {
    if (level === 'paragraph') {
      editor.chain().focus().setParagraph().run();
    } else {
      editor.chain().focus().toggleHeading({ level }).run();
    }
    setShowHeadingDropdown(false);
  };

  const currentFontFamily = () => {
    for (const font of fontOptions) {
      if (
        font !== 'Default' &&
        editor?.isActive('textStyle', { fontFamily: font })
      ) {
        return font;
      }
    }
    return 'Default';
  };

  const currentHeading = () => {
    if (editor.isActive('heading', { level: 1 })) {
      return 'Heading 1';
    }
    if (editor.isActive('heading', { level: 2 })) {
      return 'Heading 2';
    }
    if (editor.isActive('heading', { level: 3 })) {
      return 'Heading 3';
    }
    if (editor.isActive('heading', { level: 4 })) {
      return 'Heading 4';
    }
    if (editor.isActive('heading', { level: 5 })) {
      return 'Heading 5';
    }
    if (editor.isActive('heading', { level: 6 })) {
      return 'Heading 6';
    }
    return 'Paragraph';
  };

  const applyAlignment = (align: string) => {
    editor.chain().focus().setTextAlign(align).run();
    setShowAlignDropdown(false);
  };

  const currentAlignment = () => {
    if (editor.isActive({ textAlign: 'center' })) return 'Center';
    if (editor.isActive({ textAlign: 'right' })) return 'Right';
    if (editor.isActive({ textAlign: 'justify' })) return 'Justify';
    return 'Left';
  };

  const setLink = () => {
    const previousUrl = (editor?.getAttributes('link') as { href?: string })
      ?.href;
    const url = window.prompt('Enter URL', previousUrl ?? 'https://');

    if (url === null) return;

    if (url === '') {
      editor?.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor
      ?.chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url })
      .run();
  };

  const fontOptions = ['Default', 'Arial', 'Georgia', 'Courier New', 'Tahoma'];
  const fontSizeOptions = ['Default', '12px', '14px', '16px', '18px', '20px'];
  const headingOptions = [
    'Paragraph',
    'Heading 1',
    'Heading 2',
    'Heading 3',
    'Heading 4',
    'Heading 5',
    'Heading 6',
  ];
  const alignmentOptions = [
    { label: 'Left', icon: <AlignLeft size={16} /> },
    { label: 'Center', icon: <AlignCenter size={16} /> },
    { label: 'Right', icon: <AlignRight size={16} /> },
    { label: 'Justify', icon: <AlignJustify size={16} /> },
  ];

  const currentFontSize: unknown =
    editor?.getAttributes('textStyle').fontSize || 'Default';

  const highlightColors = [
    '#FFFF00',
    '#FFD700',
    '#FF6347',
    '#7CFC00',
    '#87CEEB',
    '#FFA07A',
    '#D3D3D3',
    '#FF8C00',
    '#98FB98',
    '#AFEEEE',
    '#DDA0DD',
    '#F08080',
    '#8A2BE2',
    '#A52A2A',
    '#FFC0CB',
    '#DAA520',
  ];

  const applyTextColor = (color: string) => {
    editor.chain().focus().setColor(color).run();
    setShowTextColorPalette(false);
  };

  const applyHighlightColor = (color: string) => {
    editor.chain().focus().toggleHighlight({ color }).run();
    setShowHighlightColorPalette(false);
  };

  const addTable = () => {
    editor
      .chain()
      .focus()
      .insertTable({ cols: 3, rows: 3, withHeaderRow: true })
      .run();
  };

  const headingMap: Record<string, HeadingLevel['value']> = {
    Paragraph: 'paragraph',
    'Heading 1': 1,
    'Heading 2': 2,
    'Heading 3': 3,
    'Heading 4': 4,
    'Heading 5': 5,
    'Heading 6': 6,
  };

  return (
    <div className="border rounded-md py-2 pl-1.5 pr-1 space-y-2">
      <div className="flex flex-wrap items-center gap-1 border-b pb-1 relative">
        <div className="relative flex items-center gap-1">
          <div className="relative w-28">
            <button
              data-dropdown
              type="button"
              className="w-full px-3 py-1 flex items-center justify-between border rounded text-sm text-gray-700 bg-white"
              onClick={() => setShowHeadingDropdown((prev) => !prev)}
            >
              <div className="flex items-center gap-2">{currentHeading()}</div>
              <ChevronDown size={16} />
            </button>

            {showHeadingDropdown && (
              <div
                className="absolute top-full left-0 mt-1 w-full z-10 bg-white border rounded shadow-md max-h-60 overflow-y-auto"
                data-dropdown
              >
                {headingOptions.map((item) => (
                  <button
                    key={item}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      applyHeading(headingMap[item]);
                      setShowHeadingDropdown(false);
                    }}
                  >
                    {item}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative w-28">
            <button
              data-dropdown
              type="button"
              className="w-full px-3 py-1 flex items-center justify-between border rounded text-sm text-gray-700 bg-white"
              onClick={() => setShowFontDropdown((prev) => !prev)}
            >
              <div className="flex items-center gap-2">
                {currentFontFamily()}
              </div>
              <ChevronDown size={16} />
            </button>

            {showFontDropdown && (
              <div
                data-dropdown
                className="absolute top-full left-0 mt-1 w-full z-10 bg-white border rounded shadow-md max-h-60 overflow-y-auto"
              >
                {fontOptions.map((font) => (
                  <button
                    key={font}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                    style={{
                      fontFamily: font !== 'Default' ? font : 'inherit',
                    }}
                    onClick={() => {
                      if (font === 'Default') {
                        editor?.chain().focus().unsetFontFamily().run();
                      } else {
                        editor?.chain().focus().setFontFamily(font).run();
                      }
                      setShowFontDropdown(false);
                    }}
                  >
                    {font}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative w-28">
            <button
              data-dropdown
              type="button"
              className="w-full px-3 py-1 flex items-center justify-between border rounded text-sm text-gray-700 bg-white"
              onClick={() => setShowFontSizeDropdown((prev) => !prev)}
            >
              <div className="flex items-center gap-2">
                {currentFontSize as number}
              </div>
              <ChevronDown size={16} />
            </button>

            {showFontSizeDropdown && (
              <div
                data-dropdown
                className="absolute top-full left-0 mt-1 w-full z-10 bg-white border rounded shadow-md max-h-60 overflow-y-auto"
              >
                {fontSizeOptions.map((size) => (
                  <button
                    key={size}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      if (size === 'Default') {
                        editor?.chain().focus().unsetFontSize().run();
                      } else {
                        editor?.chain().focus().setFontSize(size).run();
                      }
                      setShowFontSizeDropdown(false);
                    }}
                  >
                    {size}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative w-28">
            <button
              data-dropdown
              type="button"
              className="w-full px-3 py-1 flex items-center justify-between border rounded text-sm text-gray-700 bg-white"
              onClick={() => setShowAlignDropdown((prev) => !prev)}
            >
              <div className="flex items-center gap-2">
                {
                  alignmentOptions.find((a) => a.label === currentAlignment())
                    ?.icon
                }
                {currentAlignment()}
              </div>
              <ChevronDown size={16} />
            </button>

            {showAlignDropdown && (
              <div
                data-dropdown
                className="absolute top-full left-0 mt-1 w-full z-10 bg-white border rounded shadow-md"
              >
                {alignmentOptions.map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      applyAlignment(item.label.toLowerCase());
                      setShowAlignDropdown(false);
                    }}
                  >
                    {item.icon}
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <Button
          icon={<Bold size={16} />}
          isActive={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          label="Bold"
        />
        <Button
          icon={<Italic size={16} />}
          isActive={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          label="Italic"
        />
        <Button
          icon={<UnderlineIcon size={16} />}
          isActive={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          label="Underline"
        />
        <Button
          icon={<Strikethrough size={16} />}
          isActive={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          label="Strikethrough"
        />
        <Button
          icon={<List size={16} />}
          isActive={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          label="Bullet List"
        />
        <Button
          icon={<ListOrdered size={16} />}
          isActive={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          label="Ordered List"
        />
        <Button
          icon={<Quote size={16} />}
          isActive={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          label="Blockquote"
        />
        <Button
          icon={<Code2 size={16} />}
          isActive={editor.isActive('codeBlock')}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          label="Code Block"
        />

        <button
          type="button"
          data-dropdown
          onClick={() => setShowTextColorPalette(!showTextColorPalette)}
          className="p-2 rounded cursor-pointer hover:bg-gray-100 transition text-gray-600"
          title="Text Color"
        >
          <Baseline size={16} style={{ marginRight: 4 }} />
        </button>

        {showTextColorPalette && (
          <div data-dropdown className="color-palette text-color">
            {textColors.map((color) => (
              <button
                key={color}
                onClick={() => applyTextColor(color)}
                style={{
                  backgroundColor: color,
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  margin: 4,
                  border: '1px solid #ccc',
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>
        )}

        <button
          type="button"
          data-dropdown
          onClick={() =>
            setShowHighlightColorPalette(!showHighlightColorPalette)
          }
          className="p-2 rounded cursor-pointer hover:bg-gray-100 transition text-gray-600"
          title="Highlight Color"
        >
          <Highlighter size={16} style={{ marginRight: 4 }} />
        </button>

        {showHighlightColorPalette && (
          <div data-dropdown className="color-palette highlight-color">
            {highlightColors.map((color) => (
              <button
                key={color}
                onClick={() => applyHighlightColor(color)}
                style={{
                  backgroundColor: color,
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  margin: 4,
                  border: '1px solid #ccc',
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>
        )}

        <Button
          icon={<Minus size={16} />}
          isActive={editor.isActive('horizontalRule')}
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          label="Horizontal Rule"
        />
        <Button
          icon={<CornerDownLeft size={16} />}
          isActive={editor.isActive('hardBreak')}
          onClick={() => editor.chain().focus().setHardBreak().run()}
          label="Hard Break"
        />
        <Button
          icon={<Link2 size={16} />}
          onClick={setLink}
          isActive={editor?.isActive('link')}
          label="Link"
        />
        <Button
          icon={<TableIcon size={16} />}
          onClick={addTable}
          label="Table"
        />

        <div className="ml-auto flex gap-1">
          <Button
            icon={<Undo size={16} />}
            onClick={() => editor.chain().focus().undo().run()}
            label="Undo"
          />
          <Button
            icon={<Redo size={16} />}
            onClick={() => editor.chain().focus().redo().run()}
            label="Redo"
          />
        </div>
      </div>

      <EditorContent
        editor={editor}
        className="ProseMirror whitespace-pre-wrap px-2 py-1 h-50 overflow-y-auto focus:outline-none"
        style={{ minHeight: '200px' }}
      />

      <div className="text-right text-sm text-gray-500">
        Words: {wordCount} | Characters: {charCount}
      </div>
    </div>
  );
};

export default TextEditor;
